import React, { useState, useEffect } from 'react';
import pb from '../lib/pocketbase';
import { Package, Clock, CheckCircle, XCircle, Search, Eye, Printer, FileText, Phone, MessageSquare } from 'lucide-react';
import './AdminCrud.css';

const AdminOrders = () => {
    const [orders, setOrders] = useState([]);
    // loading state removed as it was unused in render
    const [filterStatus, setFilterStatus] = useState('all');
    const [timeFilter, setTimeFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [productImages, setProductImages] = useState({});

    useEffect(() => {
        fetchOrders();
    }, []);

    // Fetch images for items in selected order
    useEffect(() => {
        if (!selectedOrder) return;
        const fetchImages = async () => {
            try {
                const items = typeof selectedOrder.items === 'string' ? JSON.parse(selectedOrder.items) : selectedOrder.items;
                const newFoundImages = {};
                let needed = false;

                for (const item of items) {
                    // Check productImages via closure-safe way if needed, but easier to just check local batch
                    if (item.id && !productImages[item.id]) {
                        try {
                            const p = await pb.collection('patisseries').getOne(item.id, { fields: 'id,photo' });
                            if (p.photo) {
                                newFoundImages[item.id] = pb.files.getURL(p, p.photo, { thumb: '100x100' });
                                needed = true;
                            }
                        } catch (e) { /* ignore single error */ }
                    }
                }
                if (needed) {
                    setProductImages(prev => ({ ...prev, ...newFoundImages }));
                }
            } catch (err) {
                console.error("Error fetching images for order items:", err);
            }
        };
        fetchImages();
    }, [selectedOrder, productImages]); // Adding productImages here since we use functional update now or check it locally

    const fetchOrders = async () => {
        try {
            // Fetch all orders sorted by creation date (newest first)
            const records = await pb.collection('commandes').getList(1, 50, {
                sort: '-created',
            });
            setOrders(records.items);
        } catch (err) {
            console.error("Error fetching orders:", err);
            setOrders([]);
        }
    };

    const handleStatusChange = async (orderId, newStatus) => {
        try {
            await pb.collection('commandes').update(orderId, { status: newStatus });
            // Update local state
            setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
            if (selectedOrder && selectedOrder.id === orderId) {
                setSelectedOrder(prev => ({ ...prev, status: newStatus }));
            }
        } catch (err) {
            console.error("Error updating status:", err);
            alert("Erreur lors de la mise à jour.");
        }
    };

    const handleBulkStatusChange = async (newStatus) => {
        if (!window.confirm(`Passer les ${selectedIds.length} commandes sélectionnées en "${newStatus}" ?`)) return;

        try {
            const updates = selectedIds.map(id =>
                pb.collection('commandes').update(id, { status: newStatus })
            );
            await Promise.all(updates);

            setOrders(prev => prev.map(o =>
                selectedIds.includes(o.id) ? { ...o, status: newStatus } : o
            ));
            setSelectedIds([]);
        } catch (err) {
            console.error("Bulk update error:", err);
            alert("Erreur lors de la mise à jour groupée.");
        }
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === filteredOrders.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredOrders.map(o => o.id));
        }
    };

    const toggleSelectOne = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleSelectOrder = async (order) => {
        setSelectedOrder(order);
        // Mark as viewed if it wasn't already
        if (order.viewed === false) {
            try {
                await pb.collection('commandes').update(order.id, { viewed: true });
                setOrders(prev => prev.map(o => o.id === order.id ? { ...o, viewed: true } : o));
            } catch (err) {
                console.error("Error marking as viewed:", err);
            }
        }
    };

    const isUrgent = (pickupDate) => {
        if (!pickupDate) return false;
        const now = new Date();
        const pickup = new Date(pickupDate);
        const diffInMs = pickup - now;
        const diffInHours = diffInMs / (1000 * 60 * 60);
        return diffInHours > -1 && diffInHours <= 2; // Urgent if within 2h or just passed 1h
    };

    const handlePrintOrder = (order) => {
        const items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Bon de Commande #${order.id}</title>
                    <style>
                        body { font-family: sans-serif; padding: 40px; color: #333; }
                        .header { border-bottom: 2px solid #000; padding-bottom: 20px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: flex-end; }
                        h1 { margin: 0; color: #5d4037; }
                        .info { margin-bottom: 30px; line-height: 1.6; }
                        table { width: 100%; border-collapse: collapse; }
                        th { text-align: left; border-bottom: 1px solid #ddd; padding: 10px; }
                        td { padding: 10px; border-bottom: 1px solid #eee; }
                        .total { text-align: right; font-size: 1.2rem; font-weight: bold; margin-top: 20px; padding-top: 10px; border-top: 2px solid #eee; }
                        .notes { margin-top: 30px; font-style: italic; background: #f9f9f9; padding: 15px; border-left: 4px solid #e67e22; }
                        @media print { .no-print { display: none; } }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <div>
                            <h1>Maison Cédric Millet</h1>
                            <p>Artisan Pâtissier</p>
                        </div>
                        <div style="text-align: right">
                            <strong>Commande #${order.id}</strong><br>
                            Date: ${new Date(order.created).toLocaleDateString()}
                        </div>
                    </div>
                    
                    <div class="info">
                        <strong>CLIENT:</strong> ${order.client_name}<br>
                        <strong>TÉL:</strong> ${order.client_phone}<br>
                        <strong>RETRAIT PRÉVU:</strong> <span style="font-size: 1.3rem; font-weight: bold;">
                            ${order.pickup_date ? new Date(order.pickup_date).toLocaleString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </span>
                    </div>

                    <table>
                        <thead>
                            <tr>
                                <th>Produit</th>
                                <th style="text-align: center">Quantité</th>
                                <th style="text-align: right">Prix Unit.</th>
                                <th style="text-align: right">Sous-total</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${items.map(item => `
                                <tr>
                                    <td>${item.name}</td>
                                    <td style="text-align: center; font-weight: bold; font-size: 1.2rem;">${item.quantity}</td>
                                    <td style="text-align: right">${item.price.toFixed(2)} €</td>
                                    <td style="text-align: right">${(item.price * item.quantity).toFixed(2)} €</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>

                    <div class="total">TOTAL: ${order.total} €</div>

                    ${order.notes ? `<div class="notes"><strong>Note du client:</strong> ${order.notes}</div>` : ''}

                    <div style="margin-top: 100px; text-align: center; font-size: 0.8rem; color: #999;" class="no-print">
                        <button onclick="window.print()" style="padding: 10px 20px; background: #5d4037; color: white; border: none; border-radius: 5px; cursor: pointer;">Imprimer ce bon</button>
                    </div>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    const handlePrintRecap = () => {
        const recap = {};
        filteredOrders.forEach(order => {
            const items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
            items.forEach(item => {
                if (!recap[item.name]) recap[item.name] = 0;
                recap[item.name] += item.quantity;
            });
        });

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Synthèse de Production - ${timeFilter}</title>
                    <style>
                        body { font-family: sans-serif; padding: 40px; }
                        h1 { color: #5d4037; border-bottom: 2px solid #5d4037; padding-bottom: 10px; }
                        .item { display: flex; justify-content: space-between; padding: 15px 0; border-bottom: 1px solid #eee; font-size: 1.1rem; }
                        .qty { font-weight: bold; font-size: 1.4rem; color: #e67e22; }
                    </style>
                </head>
                <body>
                    <h1>Synthèse de Production (${filteredOrders.length} commandes)</h1>
                    <p>Filtre appliqué : <strong>${timeFilter === 'today' ? "Aujourd'hui" : timeFilter === 'tomorrow' ? "Demain" : timeFilter === 'week' ? "Cette semaine" : "Toutes les dates"}</strong></p>
                    
                    <div style="margin-top: 30px;">
                        ${Object.entries(recap).sort((a, b) => b[1] - a[1]).map(([name, qty]) => `
                            <div class="item">
                                <span>${name}</span>
                                <span class="qty">x ${qty}</span>
                            </div>
                        `).join('')}
                    </div>

                    <div style="margin-top: 50px; text-align: center;" class="no-print">
                        <button onclick="window.print()" style="padding: 10px 20px; border: 1px solid #5d4037; background: transparent; cursor: pointer;">Imprimer la liste</button>
                    </div>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'pending': return <span className="badge badge-warning"><Clock size={12} /> En attente</span>;
            case 'confirmed': return <span className="badge badge-info"><Package size={12} /> Confirmé</span>;
            case 'completed': return <span className="badge badge-success"><CheckCircle size={12} /> Terminé</span>;
            case 'cancelled': return <span className="badge badge-danger"><XCircle size={12} /> Annulé</span>;
            default: return status;
        }
    };

    const getFilteredOrders = () => {
        return orders.filter(order => {
            // 1. Status Filter
            if (filterStatus !== 'all' && order.status !== filterStatus) return false;

            // 2. Search Filter
            if (searchTerm) {
                const search = searchTerm.toLowerCase();
                const matchesSearch =
                    order.client_name?.toLowerCase().includes(search) ||
                    order.client_email?.toLowerCase().includes(search) ||
                    order.client_phone?.includes(search) ||
                    order.id.includes(search);
                if (!matchesSearch) return false;
            }

            // 3. Time Filter
            if (timeFilter !== 'all' && order.pickup_date) {
                const pickupDate = new Date(order.pickup_date);
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const tomorrow = new Date(today);
                tomorrow.setDate(today.getDate() + 1);

                const nextWeek = new Date(today);
                nextWeek.setDate(today.getDate() + 7);

                const orderDate = new Date(pickupDate);
                orderDate.setHours(0, 0, 0, 0);

                if (timeFilter === 'today') {
                    if (orderDate.getTime() !== today.getTime()) return false;
                } else if (timeFilter === 'tomorrow') {
                    if (orderDate.getTime() !== tomorrow.getTime()) return false;
                } else if (timeFilter === 'week') {
                    if (orderDate < today || orderDate > nextWeek) return false;
                }
            }

            return true;
        });
    };

    const filteredOrders = getFilteredOrders();

    return (
        <div className="admin-crud-container">
            <div className="admin-tools" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '15px' }}>
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                    <div className="search-bar-container" style={{ flex: 1, minWidth: '250px', position: 'relative' }}>
                        <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                        <input
                            type="text"
                            placeholder="Rechercher un nom, téléphone, email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ width: '100%', padding: '10px 10px 10px 40px', borderRadius: '8px', border: '1px solid #ddd' }}
                        />
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>
                    <div className="filter-group">
                        <span style={{ fontSize: '0.8rem', color: '#666', marginBottom: '5px', display: 'block' }}>Statut</span>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map(status => (
                                <button
                                    key={status}
                                    className={`filter-btn ${filterStatus === status ? 'active' : ''}`}
                                    onClick={() => setFilterStatus(status)}
                                    style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                                >
                                    {status === 'all' ? 'Tous' : status === 'pending' ? 'Attente' : status === 'confirmed' ? 'Confirmé' : status === 'completed' ? 'Terminé' : 'Annulé'}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="filter-group">
                        <span style={{ fontSize: '0.8rem', color: '#666', marginBottom: '5px', display: 'block' }}>Production (Retrait)</span>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button className={`filter-btn ${timeFilter === 'all' ? 'active' : ''}`} onClick={() => setTimeFilter('all')} style={{ padding: '6px 12px', fontSize: '0.85rem' }}>Toujours</button>
                            <button className={`filter-btn ${timeFilter === 'today' ? 'active' : ''}`} onClick={() => setTimeFilter('today')} style={{ padding: '6px 12px', fontSize: '0.85rem' }}>Aujourd'hui</button>
                            <button className={`filter-btn ${timeFilter === 'tomorrow' ? 'active' : ''}`} onClick={() => setTimeFilter('tomorrow')} style={{ padding: '6px 12px', fontSize: '0.85rem' }}>Demain</button>
                            <button className={`filter-btn ${timeFilter === 'week' ? 'active' : ''}`} onClick={() => setTimeFilter('week')} style={{ padding: '6px 12px', fontSize: '0.85rem' }}>7 jours</button>

                            <button
                                className="filter-btn"
                                onClick={handlePrintRecap}
                                title="Imprimer la liste de production"
                                style={{ marginLeft: '10px', display: 'flex', alignItems: 'center', gap: '5px', borderColor: '#e67e22', color: '#e67e22' }}
                            >
                                <FileText size={16} /> Synthèse
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="orders-grid" style={{ display: 'grid', gridTemplateColumns: selectedOrder ? '1fr 400px' : '1fr', gap: '20px' }}>

                {/* Orders List */}
                <div className="orders-list">
                    <table className="crud-table">
                        <thead>
                            <tr>
                                <th style={{ width: '40px' }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.length > 0 && selectedIds.length === filteredOrders.length}
                                        onChange={toggleSelectAll}
                                    />
                                </th>
                                <th>Date</th>
                                <th>Client</th>
                                <th>Retrait</th>
                                <th>Total</th>
                                <th>Statut</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOrders.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '30px' }}>Aucune commande trouvée.</td>
                                </tr>
                            ) : (
                                filteredOrders.map((order) => (
                                    <tr
                                        key={order.id}
                                        className={`${selectedOrder?.id === order.id ? 'selected-row' : ''} ${order.viewed === false ? 'new-order-row' : ''}`}
                                        onClick={() => handleSelectOrder(order)}
                                        style={{ cursor: 'pointer', position: 'relative' }}
                                    >
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.includes(order.id)}
                                                onChange={() => toggleSelectOne(order.id)}
                                            />
                                        </td>
                                        <td>
                                            {order.viewed === false && <span className="new-badge">NOUVEAU</span>}
                                            {new Date(order.created).toLocaleDateString()}
                                        </td>
                                        <td>
                                            <div style={{ fontWeight: 'bold' }}>{order.client_name}</div>
                                            <div style={{ fontSize: '0.85rem', color: '#666' }}>{order.client_phone}</div>
                                        </td>
                                        <td style={{ color: isUrgent(order.pickup_date) && order.status !== 'completed' ? '#e74c3c' : 'inherit', fontWeight: isUrgent(order.pickup_date) ? 'bold' : 'normal' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                {isUrgent(order.pickup_date) && order.status !== 'completed' && <Clock size={16} className="pulse-icon" />}
                                                {order.pickup_date ? new Date(order.pickup_date).toLocaleString('fr-FR', {
                                                    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                                                }) : '-'}
                                            </div>
                                        </td>
                                        <td style={{ fontWeight: 'bold' }}>{order.total} €</td>
                                        <td>{getStatusLabel(order.status)}</td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '5px' }}>
                                                <button
                                                    className="action-btn btn-view"
                                                    onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); }}
                                                    title="Voir détails"
                                                >
                                                    <Eye size={16} />
                                                </button>

                                                {order.status === 'pending' && (
                                                    <button
                                                        className="action-btn"
                                                        onClick={(e) => { e.stopPropagation(); handleStatusChange(order.id, 'confirmed'); }}
                                                        style={{ backgroundColor: '#2980b9' }}
                                                        title="Confirmer"
                                                    >
                                                        <CheckCircle size={16} />
                                                    </button>
                                                )}

                                                {order.status === 'confirmed' && (
                                                    <button
                                                        className="action-btn"
                                                        onClick={(e) => { e.stopPropagation(); handleStatusChange(order.id, 'completed'); }}
                                                        style={{ backgroundColor: '#27ae60' }}
                                                        title="Marquer comme récupéré"
                                                    >
                                                        <Package size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    {/* Bulk Action Bar */}
                    {selectedIds.length > 0 && (
                        <div className="bulk-action-bar" style={{
                            position: 'sticky',
                            bottom: '20px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'var(--color-brown)',
                            color: 'white',
                            padding: '12px 25px',
                            borderRadius: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '20px',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                            zIndex: 100,
                            width: 'max-content'
                        }}>
                            <span style={{ fontWeight: 'bold' }}>{selectedIds.length} sélectionné(s)</span>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button className="btn-mini" onClick={() => handleBulkStatusChange('confirmed')} style={{ background: '#2980b9', border: 'none', color: 'white', padding: '5px 15px', borderRadius: '20px', cursor: 'pointer', fontSize: '0.85rem' }}>Confirmer</button>
                                <button className="btn-mini" onClick={() => handleBulkStatusChange('completed')} style={{ background: '#27ae60', border: 'none', color: 'white', padding: '5px 15px', borderRadius: '20px', cursor: 'pointer', fontSize: '0.85rem' }}>Terminer</button>
                                <button className="btn-mini" onClick={() => setSelectedIds([])} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', padding: '5px 15px', borderRadius: '20px', cursor: 'pointer', fontSize: '0.85rem' }}>Annuler</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Order Details Panel */}
                {selectedOrder && (
                    <div className="order-details-panel" style={{ background: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #ddd', height: 'fit-content' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0 }}>Détails Commande</h3>
                            <button onClick={() => setSelectedOrder(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                                <XCircle size={20} color="#999" />
                            </button>
                        </div>

                        <div className="detail-group">
                            <label>Statut</label>
                            <div style={{ display: 'flex', gap: '10px', marginTop: '5px' }}>
                                <select
                                    value={selectedOrder.status}
                                    onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value)}
                                    style={{ padding: '8px', flex: 1, borderRadius: '4px', border: '1px solid #ddd' }}
                                >
                                    <option value="pending">En attente</option>
                                    <option value="confirmed">Confirmé (Prêt)</option>
                                    <option value="completed">Terminé (Récupéré)</option>
                                    <option value="cancelled">Annulé</option>
                                </select>
                                <button
                                    className="action-btn"
                                    onClick={() => handlePrintOrder(selectedOrder)}
                                    style={{ padding: '8px 15px', backgroundColor: '#5d4037', color: 'white', display: 'flex', alignItems: 'center', gap: '5px' }}
                                >
                                    <Printer size={16} /> Bon
                                </button>
                            </div>
                        </div>

                        <div className="detail-group" style={{ marginTop: '20px' }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Client</label>
                            <div style={{ fontSize: '1.1rem' }}>{selectedOrder.client_name}</div>
                            <div style={{ color: '#666', fontSize: '0.9rem' }}>{selectedOrder.client_email}</div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '10px' }}>
                                <span style={{ fontWeight: 'bold' }}>{selectedOrder.client_phone}</span>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <a
                                        href={`tel:${selectedOrder.client_phone}`}
                                        className="comm-btn"
                                        title="Appeler"
                                        style={{ background: '#27ae60', color: 'white', width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        <Phone size={14} />
                                    </a>
                                    <a
                                        href={`sms:${selectedOrder.client_phone}?body=${encodeURIComponent(`Bonjour ${selectedOrder.client_name}, c'est la Maison Cédric Millet. Votre commande est prête !`)}`}
                                        className="comm-btn"
                                        title="Envoyer SMS"
                                        style={{ background: '#e67e22', color: 'white', width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        <MessageSquare size={14} />
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="detail-group" style={{ marginTop: '20px' }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Retrait prévu</label>
                            <div style={{ fontSize: '1.1rem', color: 'var(--color-brown)' }}>
                                {selectedOrder.pickup_date ? new Date(selectedOrder.pickup_date).toLocaleString('fr-FR', {
                                    weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'
                                }) : '-'}
                            </div>
                        </div>

                        {selectedOrder.notes && (
                            <div className="detail-group" style={{ marginTop: '20px', background: '#fff3cd', padding: '10px', borderRadius: '4px' }}>
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Notes du client :</label>
                                <i>"{selectedOrder.notes}"</i>
                            </div>
                        )}

                        <div className="detail-group" style={{ marginTop: '20px' }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>Articles</label>
                            <div style={{ background: 'white', border: '1px solid #eee', borderRadius: '4px', maxHeight: '200px', overflowY: 'auto' }}>
                                {(() => {
                                    try {
                                        const items = typeof selectedOrder.items === 'string' ? JSON.parse(selectedOrder.items) : selectedOrder.items;
                                        return items && items.map((item, idx) => (
                                            <div key={idx} style={{ padding: '10px', borderBottom: '1px solid #f5f5f5', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                {productImages[item.id] ? (
                                                    <img src={productImages[item.id]} alt="" style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'cover' }} />
                                                ) : (
                                                    <div style={{ width: '40px', height: '40px', borderRadius: '4px', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Package size={16} color="#ccc" />
                                                    </div>
                                                )}
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: '500' }}>{item.quantity}x {item.name}</div>
                                                    <div style={{ fontSize: '0.8rem', color: '#888' }}>Prix unit: {item.price.toFixed(2)} €</div>
                                                </div>
                                                <div style={{ fontWeight: 'bold' }}>{(item.price * item.quantity).toFixed(2)} €</div>
                                            </div>
                                        ));
                                    } catch (e) {
                                        return <div style={{ padding: '10px', color: 'red' }}>Erreur de format des articles</div>;
                                    }
                                })()}
                            </div>
                            <div style={{ textAlign: 'right', marginTop: '10px', fontWeight: 'bold', fontSize: '1.2rem' }}>
                                Total: {selectedOrder.total} €
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                .filter-group {
                    display: flex;
                    gap: 10px;
                }
                .filter-btn {
                    padding: 8px 16px;
                    border: 1px solid #ddd;
                    background: white;
                    border-radius: 20px;
                    cursor: pointer;
                    font-size: 0.9rem;
                    transition: all 0.2s;
                }
                .filter-btn.active {
                    background-color: var(--color-brown);
                    color: white;
                    border-color: var(--color-brown);
                }
                .badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    padding: 4px 10px;
                    border-radius: 12px;
                    font-size: 0.85rem;
                    font-weight: 500;
                }
                .badge-warning { background-color: #fff3cd; color: #856404; }
                .badge-info { background-color: #d1ecf1; color: #0c5460; }
                .badge-success { background-color: #d4edda; color: #155724; }
                .badge-danger { background-color: #f8d7da; color: #721c24; }
                
                .selected-row {
                    background-color: #f0f0f0;
                }
                .new-order-row {
                    background-color: #fff9f0;
                }
                .new-badge {
                    background: #e67e22;
                    color: white;
                    font-size: 0.65rem;
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-weight: bold;
                    margin-right: 8px;
                    vertical-align: middle;
                }
                .pulse-icon {
                    color: #e74c3c;
                    animation: pulse 1.5s infinite;
                }
                @keyframes pulse {
                    0% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.2); opacity: 0.7; }
                    100% { transform: scale(1); opacity: 1; }
                }
                .btn-view {
                    background-color: #6c757d;
                }
                .btn-view:hover {
                    background-color: #5a6268;
                }
            `}</style>
        </div>
    );
};

export default AdminOrders;
